import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  createPatientRecordFromAppointment,
  getAppointmentClinicalContext,
  linkAppointmentPatientRecord,
  unlinkAppointmentPatientRecord,
} from '../../lib/api';
import { toast } from 'sonner';
import { AlertTriangle, FileText, Link2, Unlink, UserPlus } from 'lucide-react';

export default function ClinicalAppointmentDialog({ appointmentId, open, onOpenChange, patientName }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [busy, setBusy] = useState(false);

  const apptName =
    ctx?.appointment_patient_name ||
    ctx?.appointment?.patient_name ||
    patientName ||
    '';

  const samePhoneCount = ctx?.same_phone_expediente_count ?? 0;
  const multipleSamePhone = samePhoneCount > 1;

  useEffect(() => {
    if (!open || !appointmentId) return;
    let cancelled = false;
    setLoading(true);
    getAppointmentClinicalContext(appointmentId)
      .then((data) => {
        if (!cancelled) setCtx(data);
      })
      .catch((e) => {
        if (!cancelled) {
          const st = e?.response?.status;
          const detail = e?.response?.data?.detail;
          if (st === 404 && (!detail || detail === 'Not Found')) {
            toast.error(
              'El servidor no expone expedientes clínicos. Actualice la imagen podoclinicam-backend en la EC2.',
            );
          } else {
            toast.error(detail || 'No se pudo cargar el contexto clínico');
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, appointmentId]);

  const recordId = ctx?.patient_record_id || ctx?.patient_record?.id;

  const reloadCtx = async () => {
    const data = await getAppointmentClinicalContext(appointmentId);
    setCtx(data);
    return data;
  };

  const handleLink = async (patientRecordId) => {
    setBusy(true);
    try {
      await linkAppointmentPatientRecord(appointmentId, patientRecordId);
      toast.success('Cita vinculada al expediente');
      await reloadCtx();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo vincular');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    if (
      !window.confirm(
        '¿Desvincular esta cita del expediente? Podrá elegir otro expediente o crear uno nuevo. Las notas de evolución ya guardadas no se borran.',
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await unlinkAppointmentPatientRecord(appointmentId);
      await reloadCtx();
      toast.success('Expediente desvinculado de la cita');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo desvincular');
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    if (samePhoneCount > 0) {
      const names = (ctx?.matches || [])
        .filter((m) => m.phone_match)
        .map((m) => m.full_name)
        .filter(Boolean)
        .slice(0, 3)
        .join(', ');
      const ok = window.confirm(
        `Este teléfono ya tiene ${samePhoneCount} expediente(s) registrado(s)${names ? ` (${names})` : ''}.\n\n` +
          `¿Confirma crear un expediente NUEVO para «${apptName || 'este paciente'}»? ` +
          'Use «Vincular» si ya existe el expediente correcto.',
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await createPatientRecordFromAppointment(appointmentId, {
        link_appointment: true,
      });
      if (r?.warning) {
        toast.warning(r.warning, { duration: 8000 });
      }
      toast.success('Expediente creado y vinculado');
      const rid = r?.patient_record?.id;
      if (rid) navigate(`/admin/expedientes/${rid}?appointment=${appointmentId}`);
      onOpenChange(false);
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (st === 404 && (!detail || detail === 'Not Found')) {
        toast.error(
          'Ruta de expedientes no disponible en el API. Ejecute pull/recreate de podoclinicam-backend:latest.',
        );
      } else {
        toast.error(detail || 'No se pudo crear el expediente');
      }
    } finally {
      setBusy(false);
    }
  };

  const openRecord = () => {
    if (!recordId) return;
    navigate(`/admin/expedientes/${recordId}?appointment=${appointmentId}`);
    onOpenChange(false);
  };

  const matches = ctx?.matches || [];
  const bestMatchId = useMemo(
    () => matches.find((m) => m.recommended)?.patient_record_id,
    [matches],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Historia clínica
          </DialogTitle>
          <DialogDescription>
            Vincule por <strong>nombre del paciente en la cita</strong>, no solo por teléfono. Un mismo
            celular puede tener varios expedientes (familia).
          </DialogDescription>
        </DialogHeader>

        {apptName ? (
          <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Paciente en esta cita: </span>
            <strong>{apptName}</strong>
            {ctx?.appointment?.patient_phone ? (
              <span className="text-muted-foreground"> · {ctx.appointment.patient_phone}</span>
            ) : null}
          </div>
        ) : null}

        {multipleSamePhone ? (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            Hay {samePhoneCount} expedientes con este teléfono. Elija el que coincida con el nombre de la
            cita o cree uno nuevo si es otra persona.
          </p>
        ) : null}

        {loading ? (
          <p className="text-muted-foreground py-6 text-center">Cargando…</p>
        ) : recordId ? (
          <div className="space-y-4">
            <Badge variant="secondary">Expediente vinculado</Badge>
            {ctx?.patient_record?.patient ? (
              <div className="text-sm space-y-1">
                <p>
                  <span className="text-muted-foreground">Expediente: </span>
                  <strong>{ctx.patient_record.patient.full_name}</strong>
                </p>
                {apptName &&
                ctx.patient_record.patient.full_name &&
                apptName.trim().toLowerCase() !==
                  ctx.patient_record.patient.full_name.trim().toLowerCase() ? (
                  <p className="text-xs text-amber-800">
                    El nombre en la cita («{apptName}») no coincide exactamente con el expediente.
                  </p>
                ) : null}
                {ctx.patient_record.patient.phone ? (
                  <p className="text-xs text-muted-foreground">{ctx.patient_record.patient.phone}</p>
                ) : null}
              </div>
            ) : null}
            {ctx?.encounter ? (
              <p className="text-xs text-muted-foreground">Ya hay una nota de evolución registrada para esta cita.</p>
            ) : (
              <p className="text-xs text-muted-foreground">Puede iniciar la nota de evolución de esta visita en el expediente.</p>
            )}
            <Button className="w-full rounded-full" onClick={openRecord}>
              Abrir expediente
            </Button>
            <Button
              variant="outline"
              className="w-full rounded-full gap-2 text-muted-foreground"
              disabled={busy}
              onClick={handleUnlink}
            >
              <Unlink className="w-4 h-4" />
              Cambiar o desvincular expediente
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Compare el nombre de la cita con cada expediente sugerido antes de vincular.
            </p>
            {matches.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">Coincidencias sugeridas</p>
                {matches.map((m) => {
                  const isBest = m.patient_record_id === bestMatchId && m.recommended;
                  return (
                    <div
                      key={m.patient_record_id}
                      className={`flex items-center justify-between gap-2 rounded-lg border p-3 ${
                        isBest ? 'border-primary/50 bg-primary/5' : 'bg-muted/30'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium truncate">{m.full_name || '—'}</p>
                          {isBest ? (
                            <Badge variant="default" className="text-xs shrink-0">
                              Mejor por nombre
                            </Badge>
                          ) : null}
                          {m.phone_match && multipleSamePhone ? (
                            <Badge variant="outline" className="text-xs shrink-0">
                              Mismo teléfono
                            </Badge>
                          ) : null}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {m.phone || m.phone_digits_10 || 'Sin teléfono'}
                          {m.name_score != null ? ` · nombre ${Math.round(m.name_score)} pts` : ''}
                        </p>
                        {apptName && m.full_name ? (
                          <p className="text-xs mt-1">
                            <span className="text-muted-foreground">Cita: </span>
                            {apptName}
                            <span className="text-muted-foreground"> → Expediente: </span>
                            {m.full_name}
                          </p>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        variant={isBest ? 'default' : 'outline'}
                        disabled={busy}
                        onClick={() => handleLink(m.patient_record_id)}
                      >
                        <Link2 className="w-4 h-4 mr-1" />
                        Vincular
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No hay coincidencias por teléfono o nombre.</p>
            )}
            {samePhoneCount > 0 ? (
              <p className="text-xs text-muted-foreground">
                Si «{apptName || 'este paciente'}» no aparece arriba, use crear nuevo expediente (no reutilice
                el de otra persona solo por el teléfono).
              </p>
            ) : null}
            <Button className="w-full rounded-full gap-2" disabled={busy} onClick={handleCreate}>
              <UserPlus className="w-4 h-4" />
              Crear nuevo expediente
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
